import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, skip, timeout} from 'rxjs/operators';
import {WebService} from "./web/web.service";
import {LoggerService} from "./logger.service";
import {PinValidationService} from "./pin-validation.service";
import {InactivityMonitorService} from "./inactivity-monitor.service";
import {AuthStateService, AuthenticatedSession} from './auth/auth-state.service';
import {SessionStoreService} from './session-store.service';
import {PinValidationResponse} from '../models/pin-validation.model';

export class PinResendSessionError extends Error {
    constructor() {
        super('No se pudo restaurar la identidad de la sesión');
        this.name = 'PinResendSessionError';
    }
}

interface PinCredentialSnapshot {
    email: string | null;
    partner: number | null;
    sessionId: string | null;
    accessToken: string | null;
    refreshToken: string | null;
}

export interface PinAuthContext {
    email: string | null;
    partner: number | null;
    sessionId: string | null;
    authRevision: number;
}

@Injectable({
    providedIn: 'root'
})
export class SecurityService {
    private tokenKey = 'authToken';
    private refreshKey = 'refresToken';
    private pinValidatedKey = 'pinValidated';
    private pendingMfaTokenKey = 'pendingMfaJwt';
    private loginUrl = this.webService.HOST + '/auth/login';
    private refreshUrl = this.webService.HOST + '/auth/refresh';
    private logoutUrl = this.webService.HOST + '/auth/logout';
    private readonly logoutTimeoutMs = 5000;
    private logoutInProgress = false;
    private refreshInFlight$: Observable<any> | null = null;
    private authRevision = 0;
    private readonly authContextSubject = new BehaviorSubject<PinAuthContext>({
        email: null, partner: null, sessionId: null, authRevision: 0
    });
    readonly authContextChanges$ = this.authContextSubject.asObservable();

    private logger: LoggerService;
    private pinValidationService: PinValidationService;
    private inactivityMonitor: InactivityMonitorService;

    constructor(
        private webService: WebService, 
        private http: HttpClient, 
        private router: Router,
        pinValidationService: PinValidationService,
        inactivityMonitor: InactivityMonitorService,
        private authState: AuthStateService,
        private sessionStore: SessionStoreService
    ) {
        this.logger = new LoggerService();
        this.pinValidationService = pinValidationService;
        this.inactivityMonitor = inactivityMonitor;
        combineLatest([
            this.authState.accessToken$,
            this.authState.refreshToken$,
            this.authState.sessionId$
        ]).pipe(skip(1)).subscribe(() => this.publishPinAuthContext(false));
    }

    login(username: string, password: string): Observable<any> {
        return this.http.post<any>(
            this.loginUrl,
            { username, password, partner: null },
            { withCredentials: true }
        ).pipe(
            map(response => {
                if (response.jwt) {
                    this.storeSession(response);
                    if (response.mfaRequired !== undefined) {
                        this.isPinValidated(response.mfaRequired !== true);
                    } else {
                        this.isPinValidated(false);
                    }
                    if (response.mfaRequired === true) {
                        this.savePendingMfaToken(response.jwt);
                    } else {
                        this.clearPendingMfaToken();
                    }
                }
                return response;
            }),
            catchError(this.handleError)
        );
    }

    refreshToken(): Observable<any> {
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        const request$ = this.createRefreshRequest().pipe(
            finalize(() => {
                if (this.refreshInFlight$ === request$) {
                    this.refreshInFlight$ = null;
                }
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.refreshInFlight$ = request$;
        return request$;
    }

    resendPinForCurrentSession(): Observable<void> {
        const credentials = this.capturePinCredentials();
        if (credentials.refreshToken && !this.isRefreshCredentialCompatible(credentials.refreshToken, credentials)) {
            return throwError(() => new PinResendSessionError());
        }
        const headers = credentials.refreshToken
            ? new HttpHeaders().set('Authorization', `Bearer ${credentials.refreshToken}`)
            : undefined;

        return this.http.post<void>(
            this.webService.HOST + '/auth/resend-pin',
            {},
            { headers, withCredentials: true }
        ).pipe(
            catchError((error: HttpErrorResponse) => error.status === 401
                ? throwError(() => new PinResendSessionError())
                : throwError(() => error))
        );
    }

    validatePinForCurrentSession(pin: string, attempt?: number): Observable<PinValidationResponse> {
        const credentials = this.capturePinCredentials();
        if (!credentials.email ||
            (credentials.refreshToken && !this.isRefreshCredentialCompatible(credentials.refreshToken, credentials))) {
            return throwError(() => new PinResendSessionError());
        }

        const body: { email: string; pin: string; partner: any; sessionId?: string; attempt?: number } = {
            email: credentials.email,
            pin,
            partner: credentials.partner
        };
        if (credentials.sessionId) body.sessionId = credentials.sessionId;
        if (attempt !== undefined) body.attempt = attempt;

        return this.http.post<PinValidationResponse>(
            this.webService.HOST + '/auth/validate-pin', body, { withCredentials: true }
        ).pipe(
            catchError((error: HttpErrorResponse) =>
                error.status === 401 && error.error?.code === 'SESSION_INVALID'
                    ? throwError(() => new PinResendSessionError())
                    : throwError(() => error)
            )
        );
    }

    private capturePinCredentials(): PinCredentialSnapshot {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.refreshKey);
        const accessToken = this.getPinAccessToken();
        const accessPayload = accessToken ? this.decodeToken(accessToken) : null;
        const accessIdentity = accessPayload?.type !== 'refresh'
            ? this.extractPinIdentity(accessPayload)
            : null;
        const refreshToken = this.authState.getRefreshToken();
        const refreshIdentity = refreshToken && this.isValidOnboardingRefreshToken(refreshToken)
            ? this.extractPinIdentity(this.decodeToken(refreshToken))
            : null;

        if (refreshToken && refreshIdentity) {
            if (!accessIdentity) {
                return { ...refreshIdentity, accessToken, refreshToken };
            }
            if (this.arePinIdentitiesEqual(accessIdentity, refreshIdentity)) {
                return { ...accessIdentity, accessToken, refreshToken };
            }
        }

        return {
            email: accessIdentity?.email ?? null,
            partner: accessIdentity?.partner ?? null,
            sessionId: accessIdentity?.sessionId ?? null,
            accessToken,
            refreshToken
        };
    }

    private getPinAccessToken(): string | null {
        return this.authState.getAccessToken();
    }

    private extractPinIdentity(payload: any | null): Pick<PinCredentialSnapshot, 'email' | 'partner' | 'sessionId'> | null {
        const email = typeof payload?.sub === 'string' && payload.sub.trim() !== '' ? payload.sub : null;
        if (!email) return null;
        return {
            email,
            partner: null,
            sessionId: typeof payload.session_id === 'string' && payload.session_id.trim() !== ''
                ? payload.session_id
                : null
        };
    }

    private isValidRefreshToken(token: string): boolean {
        const payload = this.decodeToken(token);
        return !!payload && payload.type === 'refresh' &&
            typeof payload.exp === 'number' && payload.exp > Math.floor(Date.now() / 1000);
    }

    private isValidOnboardingRefreshToken(token: string): boolean {
        const payload = this.decodeToken(token);
        return this.isValidRefreshToken(token) && payload?.partner === null;
    }

    private isRefreshCredentialCompatible(token: string, snapshot: PinCredentialSnapshot): boolean {
        if (!this.isValidOnboardingRefreshToken(token)) return false;
        const refreshIdentity = this.extractPinIdentity(this.decodeToken(token));
        return !!refreshIdentity && this.arePinIdentitiesEqual(refreshIdentity, snapshot);
    }

    private arePinIdentitiesEqual(
        left: Pick<PinCredentialSnapshot, 'email' | 'partner' | 'sessionId'>,
        right: Pick<PinCredentialSnapshot, 'email' | 'partner' | 'sessionId'>
    ): boolean {
        return left.email === right.email && left.sessionId === right.sessionId;
    }

    private createRefreshRequest(): Observable<any> {
        const username = this.getTokenSub() || 'cookie-refresh@billcentrix.local';
        const randomPassword = Math.floor(Math.random() * 900000) + 100000;

        return this.http.post<any>(
            this.refreshUrl,
            { username, password: randomPassword, partner: null },
            { withCredentials: true }
        ).pipe(
            map(response => {
                if (!response?.jwt ||
                    typeof response.refreshToken !== 'string' ||
                    response.refreshToken.trim() === '') {
                    this.authState.clearSession();
                    throw new HttpErrorResponse({
                        status: 401,
                        statusText: 'Invalid refresh response',
                        error: {
                            error: 'INVALID_REFRESH_RESPONSE',
                            message: 'Refresh response must rotate both access and refresh tokens'
                        }
                    });
                }

                this.storeSession(response, true);
                if (response.mfaRequired !== undefined) {
                    this.isPinValidated(response.mfaRequired !== true);
                }
                return response;
            }),
            catchError((error: HttpErrorResponse) => {
                if ((error.status === 403 && error.error?.error === 'MFA_REQUIRED') ||
                    error.error?.error === 'INVALID_REFRESH_RESPONSE') {
                    return throwError(() => error);
                }
                return this.handleError(error);
            })
        );
    }

    saveToken(token: string, publish = true): void {
        const payload = this.decodeToken(token);
        const session: AuthenticatedSession = {
            accessToken: token,
            sessionId: payload?.session_id ?? null,
            user: payload ? payload.user : null,
            permissions: this.extractPermissions(payload)
        };
        if (this.authState.hasMfaStatus()) {
            session.mfaVerified = this.authState.isMfaVerified();
        }
        this.authState.setAuthenticatedSession(session);
        localStorage.removeItem(this.tokenKey);
        if (publish) this.publishPinAuthContext(true);
    }

    storeSession(response: {
        jwt: string;
        refreshToken?: string | null;
        sessionId?: string | null;
        mfaRequired?: boolean;
    }, preserveAuthRevision = false): void {
        const tokenSessionId = this.decodeToken(response.jwt)?.session_id ?? null;
        if (response.sessionId && tokenSessionId && response.sessionId !== tokenSessionId) {
            throw new Error('Session id inconsistente');
        }
        this.saveToken(response.jwt, false);
        this.authState.setSessionId(response.sessionId ?? tokenSessionId);
        this.saveRefresh(response.refreshToken ?? null);
        this.publishPinAuthContext(!preserveAuthRevision);
    }

    restorePendingMfaToken(): boolean {
        if (this.getToken()) {
            return false;
        }

        const token = sessionStorage.getItem(this.pendingMfaTokenKey);
        if (!token) {
            return false;
        }

        this.saveToken(token);
        this.isPinValidated(false);
        return true;
    }

    clearPendingMfaToken(): void {
        sessionStorage.removeItem(this.pendingMfaTokenKey);
    }

    private savePendingMfaToken(token: string): void {
        sessionStorage.setItem(this.pendingMfaTokenKey, token);
    }

    getToken(): string | null {
        localStorage.removeItem(this.tokenKey);
        return this.authState.getAccessToken();
    }

    isTokenExpired(): boolean {
        const token = this.getToken();

        if (!token) {
            return true;
        }

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(new Date().getTime() / 1000);
            const expirationTime = payload.exp;
            return expirationTime < currentTime;
        } catch (error) {
            return true;
        }
    }

    isPinValidated(validated: boolean): void {
        this.authState.setMfaVerified(validated);
        localStorage.removeItem(this.pinValidatedKey);
        window.dispatchEvent(new CustomEvent('pinValidationChanged', { 
            detail: { validated } 
        }));
    }

    getPinValidationStatus(): boolean {
        if (this.authState.hasMfaStatus()) {
            return this.authState.isMfaVerified();
        }
        return localStorage.getItem(this.pinValidatedKey) === 'true';
    }

    private decodeTokenPayload(): any | null {
        const token = this.getToken();
        if (!token) return null;

        return this.decodeToken(token);
    }

    private decodeToken(token: string): any | null {
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (error) {
            return null;
        }
    }

    private extractPermissions(payload: any | null): string[] {
        if (typeof payload?.permissions === 'string') {
            return payload.permissions.split(',').map((permission: string) => permission.trim());
        }
        if (Array.isArray(payload?.permissions)) {
            return payload.permissions;
        }
        return [];
    }

    getTokenConfigs(): any | null {
        const payload = this.decodeTokenPayload();
        return payload ? payload.config : null;
    }

    getTokenPartner(): any | null {
        if (this.isSuperAdmin()) {
            const sesion = this.sessionStore.getSesion();
            if (sesion && sesion.partner != null) {
                return sesion.partner;
            }
            return null;
        }
        const payload = this.decodeTokenPayload();
        return payload ? payload.partner : null;
    }

    getTokenSub(): any | null {
        const payload = this.decodeTokenPayload();
        return payload ? payload.sub : null;
    }

    getTokenCustomer(): any | null {
        if (this.isSuperAdmin()) {
            const sesion = this.sessionStore.getSesion();
            if (sesion && sesion.customer != null) {
                return sesion.customer;
            }
            return null;
        }
        const payload = this.decodeTokenPayload();
        return payload ? payload.customer : null;
    }

    getTokenUser(): any | null {
        const payload = this.decodeTokenPayload();
        return payload ? payload.user : null;
    }

    getTokenAdminOrUser(): any | null {
        const payload = this.decodeTokenPayload();
        if (!payload) {
            return null;
        }
        return payload.userAdminId != null ? payload.userAdminId : payload.user;
    }

    getTokenPermissions(): string | string[] | null {
        const payload = this.decodeTokenPayload();
        return payload ? payload.permissions : null;
    }

    hasRole(role: string): boolean {
        const normalizedPermissions = this.authState.getPermissions();
        if (normalizedPermissions.length > 0) {
            return normalizedPermissions.includes(role);
        }

        const permissions = this.getTokenPermissions();
        const roles = Array.isArray(permissions)
            ? permissions
            : typeof permissions === 'string'
                ? permissions.split(',').map(permission => permission.trim())
                : [];
        return roles.includes(role);
    }

    isOnboardingAdmin(): boolean {
        return this.hasRole('ROLE_onboarding_user_admin');
    }

    isSuperAdmin(): boolean {
        const payload = this.decodeTokenPayload();
        return payload?.isAdmin === true && this.hasRole('ROLE_admin');
    }

    getRefreshToken(): string | null {
        localStorage.removeItem(this.refreshKey);
        return this.authState.getRefreshToken();
    }

    capturePinAuthContext(): PinAuthContext {
        const snapshot = this.capturePinCredentials();
        return {
            email: snapshot.email,
            partner: snapshot.partner,
            sessionId: snapshot.sessionId,
            authRevision: this.authRevision
        };
    }

    logout(): void {
        if (this.logoutInProgress) {
            return;
        }
        this.logoutInProgress = true;

        this.http.post(this.logoutUrl, {}, { withCredentials: true }).pipe(
            timeout(this.logoutTimeoutMs)
        ).subscribe({
            next: () => this.navigateToLogin(),
            error: () => this.navigateToLogin()
        });
        this.pinValidationService.resetState();
        this.inactivityMonitor.reset();
        this.authState.clearSession();
        this.publishPinAuthContext(true);
        this.sessionStore.clear();
        localStorage.clear();
        sessionStorage.clear();
        
        try {
            if (typeof (window as any).google !== 'undefined' && 
                (window as any).google?.accounts?.id) {
                (window as any).google.accounts.id.disableAutoSelect();
            }
            
            if (typeof (window as any).gapi !== 'undefined' && 
                (window as any).gapi?.auth2) {
                const authInstance = (window as any).gapi.auth2.getAuthInstance();
                if (authInstance) {
                    authInstance.signOut();
                }
            }
        } catch (error) {
            // Silently handle Google logout errors
        }
    }

    private navigateToLogin(): void {
        this.router.navigate(['/login']).then(
            () => {
                this.logoutInProgress = false;
                this.reloadPage();
            },
            () => {
                this.logoutInProgress = false;
            }
        );
    }

    private reloadPage(): void {
        window.location.reload();
    }

    private handleError(error: HttpErrorResponse) {
        let errorMsg = 'Error desconocido';
        
        if (error.error instanceof ErrorEvent) {
            errorMsg = `Error: ${error.error.message}`;
        } else {
            switch (error.status) {
                case 401:
                    errorMsg = 'Usuario o contraseña incorrectos. Verifica tus datos o restablece tu contraseña.';
                    break;
                case 404:
                    if (error.error?.message && error.error.message.includes('not found')) {
                        errorMsg = 'Usuario no encontrado. Por favor, verifica tu correo electrónico o regístrate.';
                    } else {
                        errorMsg = 'Recurso no encontrado';
                    }
                    break;
                case 403:
                    errorMsg = 'Acceso denegado. No tienes permisos para realizar esta acción.';
                    break;
                case 500:
                    errorMsg = 'Error interno del servidor. Por favor, intenta más tarde.';
                    break;
                case 503:
                    errorMsg = 'Servicio no disponible. Por favor, intenta más tarde.';
                    break;
                case 0:
                    errorMsg = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
                    break;
                default:
                    if (error.error?.message) {
                        errorMsg = error.error.message;
                    } else {
                        errorMsg = `Error ${error.status}: ${error.message || 'Error desconocido'}`;
                    }
            }
        }
        
        return throwError(() => new Error(errorMsg));
    }

    public getDecodedToken(): any | null {
        const token = this.getToken();
        
        if (!token) {
            return null;
        }

        try {
            const parts = token.split('.');
            
            if (parts.length !== 3) {
                return null;
            }

            const decodedToken = {
                header: JSON.parse(atob(parts[0])),
                payload: JSON.parse(atob(parts[1])),
                signature: parts[2]
            };

            return decodedToken;
        } catch (error) {
            return null;
        }
    }

    private saveRefresh(refresh: string | null): void {
        this.authState.setRefreshToken(refresh);
        localStorage.removeItem(this.refreshKey);
    }

    private publishPinAuthContext(forceRevision: boolean): void {
        const snapshot = this.capturePinCredentials();
        const previous = this.authContextSubject.value;
        const changed = previous.email !== snapshot.email ||
            previous.partner !== snapshot.partner ||
            previous.sessionId !== snapshot.sessionId;
        if (!forceRevision && !changed) return;
        if (forceRevision || changed) this.authRevision++;
        this.authContextSubject.next({
            email: snapshot.email,
            partner: snapshot.partner,
            sessionId: snapshot.sessionId,
            authRevision: this.authRevision
        });
    }

}
